import { Link } from "react-router-dom";
import useUserBrands from "@/hooks/queries/useUserBrands";
import { useAuth } from "@/hooks/useAuth";
import {
  Building2,
  Plus,
  ShieldCheck,
  UserCheck,
  ArrowRight,
  FolderKanban,
  HousePlus,
  // Users,
  // LayoutDashboard,
  // Settings,
} from "lucide-react";
import { getMuciifiOrigin } from "@/lib/origin";
import { Button } from "@/components/ui";
import { motion } from "framer-motion"; // optional but nice, if not installed we can use CSS

// If framer-motion is not installed, we can use inline CSS animations
// For this example, I'll use Tailwind's animate classes and a simple fade-in

export default function WorkspaceLanding() {
  const { data: brands = [], isLoading } = useUserBrands();
  const { user, isAuthenticated } = useAuth();

  const handleBrandSelect = (slug: string) => {
    // 1. Parse the original base url structure
    const url = new URL(getMuciifiOrigin());

    // 2. Attach the subdomain.
    // (Result: "://muciifidev.com" or "workspace.localhost")
    url.hostname = `workspace.${url.hostname}`;

    // 3. Attach the workspace brand slug path (Result: "/brandSlug")
    url.pathname = `/${slug}`;

    // 4. FIX: Use standard browser redirection, converting the URL object to a string
    window.location.href = url.toString();
  };
  const handleSignInClick = () => {
    window.location.href = `${getMuciifiOrigin()}/auth/login?returnTo=${window.location.href}`;
  };

  // Animation variants for cards
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.4,
        ease: "easeOut" as const,
      },
    }),
  };

  return (
    <div className="min-h-screen bg-linear-to-br from-slate-50 via-white to-blue-50/30 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-slate-200/80 bg-white/80 shadow-sm backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <div className="from-brand-primary to-brand-accent shadow-status-info-border flex h-12 w-12 items-center justify-center rounded bg-linear-to-br text-white shadow-lg">
              <HousePlus className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                Workspace Selection
              </h1>
              <p className="text-sm text-slate-500">
                Choose a brand workspace to manage properties and settings
              </p>
            </div>
          </div>

          <div>
            {isAuthenticated ? (
              <>
                <div>
                  Signed in as {user?.name} (
                  <span
                    onClick={handleSignInClick}
                    className={"cursor-pointer underline"}
                  >
                    Change
                  </span>
                  )
                </div>
              </>
            ) : (
              <>
                <Button onClick={handleSignInClick}>
                  Sign in to see your brands
                </Button>
              </>
            )}
          </div>

          <Link to="/chooser/new-brand">
            <button className="group inline-flex items-center gap-2 rounded bg-linear-to-r from-sky-500 to-sky-400 px-5 py-3 text-sm font-semibold text-white shadow-md transition-all hover:scale-105 hover:shadow-lg active:scale-95">
              <Plus className="h-5 w-5 transition-transform group-hover:rotate-90" />
              <span>Create New Brand</span>
            </button>
          </Link>
        </div>
      </div>

      <main className="mx-auto mt-10 max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Welcome Section with decorative elements */}
        <div className="from-brand-primary to-brand-accent relative mb-12 overflow-hidden rounded-lg bg-linear-to-r p-8 text-white shadow-xl">
          <div className="absolute top-0 right-0 -mt-16 -mr-16 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute bottom-0 left-0 -mb-16 -ml-16 h-64 w-64 rounded-full bg-white/5 blur-2xl" />
          <div className="relative flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Welcome back, {user?.name?.split(" ")[0] || "User"}!
              </h2>
              <p className="text-brand-primary-muted mt-1">
                Select a brand workspace to start managing your properties.
              </p>
            </div>
            <div className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 backdrop-blur-sm">
              <FolderKanban className="h-4 w-4 text-yellow-300" />
              <span className="text-sm font-medium">
                {brands.length}{" "}
                {brands.length === 1 ? "Workspace" : "Workspaces"}
              </span>
            </div>
          </div>
        </div>

        {/* Loading State Skeleton with shimmer */}
        {isLoading && (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="relative h-56 overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm"
              >
                <div className="animate-shimmer absolute inset-0 -translate-x-full bg-linear-to-r from-transparent via-slate-100/50 to-transparent" />
                <div className="flex items-start justify-between">
                  <div className="h-14 w-14 rounded-2xl bg-slate-200" />
                  <div className="h-6 w-20 rounded-full bg-slate-200" />
                </div>
                <div className="mt-5 space-y-3">
                  <div className="h-6 w-3/4 rounded bg-slate-200" />
                  <div className="h-4 w-full rounded bg-slate-100" />
                  <div className="h-4 w-2/3 rounded bg-slate-100" />
                </div>
                <div className="absolute right-6 bottom-6 left-6 flex items-center justify-between">
                  <div className="h-4 w-20 rounded bg-slate-200" />
                  <div className="h-4 w-24 rounded bg-slate-200" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && brands.length === 0 && (
          <div className="mx-auto max-w-md rounded-3xl border border-dashed border-slate-300 bg-white p-12 text-center shadow-lg transition-all hover:shadow-xl">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-3xl bg-linear-to-br from-blue-50 to-indigo-50 text-blue-600">
              <Building2 className="h-10 w-10" />
            </div>
            <h3 className="mt-6 text-2xl font-bold text-slate-900">
              No Brands Found
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              You aren't associated with any brand workspace yet. Create a new
              brand workspace to get started.
            </p>
            <div className="mt-8">
              <Link to="/chooser/new-brand">
                <button className="inline-flex items-center gap-2 rounded bg-linear-to-r from-blue-600 to-sky-500 px-6 py-3 text-sm font-semibold text-white shadow-md shadow-blue-200 transition-all hover:scale-105 hover:shadow-lg active:scale-95">
                  <Plus className="h-5 w-5" />
                  <span>Create Brand</span>
                </button>
              </Link>
            </div>
          </div>
        )}

        {/* Brands Grid with animation */}
        {!isLoading && brands.length > 0 && (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {brands.map((brand, index) => {
              const isOwner = user?.id === brand.ownerUserId;
              const bm = brand?.members?.find((m) => m?.userId === user?.id);
              const roleName = bm?.role?.roleName || "Member";

              return (
                <motion.div
                  key={brand.id}
                  custom={index}
                  initial="hidden"
                  animate="visible"
                  variants={cardVariants}
                  onClick={() => handleBrandSelect(brand.slug)}
                  className="group relative flex cursor-pointer flex-col justify-between overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-300 hover:shadow-xl hover:shadow-blue-500/10"
                >
                  {/* Subtle gradient overlay on hover */}
                  <div className="absolute inset-0 bg-linear-to-br from-blue-50/0 via-blue-50/0 to-blue-50/0 transition-all duration-300 group-hover:from-blue-50/30 group-hover:via-blue-50/10 group-hover:to-blue-50/0" />

                  <div className="relative">
                    <div className="flex items-start justify-between gap-4">
                      {brand.logoUrl ? (
                        <img
                          src={brand.logoUrl}
                          alt={brand.name}
                          className="h-16 w-16 rounded-2xl border border-slate-100 object-cover shadow-sm transition-transform group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-linear-to-br from-slate-700 to-slate-900 text-2xl font-black text-white shadow-md">
                          {brand.name?.charAt(0).toUpperCase() || "B"}
                        </div>
                      )}

                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold tracking-wide ${
                          isOwner
                            ? "border border-blue-200/60 bg-blue-50 text-blue-700"
                            : "border border-slate-200 bg-slate-100 text-slate-700"
                        }`}
                      >
                        {isOwner ? (
                          <>
                            <ShieldCheck className="h-3.5 w-3.5 text-blue-600" />
                            Owner
                          </>
                        ) : (
                          <>
                            <UserCheck className="h-3.5 w-3.5 text-slate-500" />
                            {roleName}
                          </>
                        )}
                      </span>
                    </div>

                    <div className="mt-5">
                      <h3 className="text-xl font-bold text-slate-900 transition-colors group-hover:text-blue-600">
                        {brand.name}
                      </h3>
                      <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-slate-500">
                        {brand.description ||
                          `Manage workspace, listings, and analytics for ${brand.name}.`}
                      </p>
                    </div>
                  </div>

                  <div className="relative mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
                    <span className="text-xs font-medium text-slate-400">
                      {brand.propertyCount !== undefined
                        ? `${brand.propertyCount} Properties`
                        : "Workspace Active"}
                    </span>

                    <div className="flex items-center gap-1 text-sm font-bold text-blue-600 transition-all group-hover:translate-x-1">
                      <span>Enter Workspace</span>
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </div>
                  </div>

                  {/* Decorative corner accent */}
                  <div className="absolute top-0 right-0 h-16 w-16 overflow-hidden">
                    <div className="absolute -top-8 -right-8 h-16 w-16 rotate-45 bg-linear-to-br from-blue-200/20 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>

      {/* Inline CSS for shimmer animation (no config needed) */}
      <style>{`
        @keyframes shimmer {
          100% {
            transform: translateX(100%);
          }
        }
        .animate-shimmer {
          animation: shimmer 1.5s infinite;
        }
      `}</style>
    </div>
  );
}
